import React, { Fragment, memo, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Collapse } from 'react-collapse'
import { FilterFromServer } from '../App'
import { FieldCheckBox, Icon, IconLarge } from '.'
import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus'
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'
import { faFilter } from '@fortawesome/pro-light-svg-icons/faFilter'
import { useMediaQuery } from '@react-hook/media-query'
import { Translate } from '../TranslationsContext'

const FilterWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;

  span {
    text-align: left;
  }
`

type FilterSectionProps = {
  title: string
  initialOpen: boolean
  // todo
}

const FilterSectionToggle = styled.button<{ isOpen: boolean }>`
  display: flex;
  width: 100%;
  border: 0;
  border-bottom: 1px solid #ececec;
  align-items: center;
  background: transparent;
  padding: 8px 0 16px 0;
  outline: none;
  cursor: pointer;

  ${(p) =>
    p.isOpen &&
    css`
      border-bottom-color: transparent;
    `}

  span {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
  }
`

const FilterSection: React.FC<FilterSectionProps> = ({ title, initialOpen, children }) => {
  const [isOpen, setIsOpen] = useState(initialOpen)

  return (
    <FilterWrapper>
      <FilterSectionToggle isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <span>{title}</span>
        <div style={{ flex: 1 }} />
        <Icon icon={isOpen ? faMinus : faPlus} />
      </FilterSectionToggle>
      <Collapse isOpened={isOpen}>{children}</Collapse>
    </FilterWrapper>
  )
}

const ButtonGhost = styled.button`
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 3px rgba(236, 236, 236, 0.56);
  border-radius: 12px;
  color: #56a0d3;
  font-size: 10px;
  line-height: 10px;
`

const Wrapper = styled.div`
  .ReactCollapse--collapse {
    transition: height 200ms;
  }
`

const Header = styled.header`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ececec;
  padding-bottom: 8px;
  outline: none;
  width: 100%;
  border-top: none;
  border-left: none;
  border-right: none;
  background: none;
  min-height: 41px;

  span {
    text-align: left;
    font-family: Roboto;
    font-size: 16px;
  }
`

const HeaderButton = styled.button`
  display: flex;
  border: none;
  outline: none;
  background: none;
  flex: 1 1;
`

type FilterMenuProps = {
  filters: Array<FilterFromServer>
  activeFilters: number[]
  setActiveFilters: (newActiveFilters: number[]) => void
  clearAll: () => void
}

const NoFiltersText = styled.span`
  display: block;
  margin: 16px 0;
  color: #939393;
`

const FilterCount = styled.div`
  margin: -1px 0 0 8px;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-style: normal;
  font-weight: 400;
  color: #303030;

  background: #eee;
  line-height: 1;
  padding: 2px 5px;
  align-self: baseline;
  border-radius: 4px;
`

const Row = styled.div`
  display: flex;
`

function groupBy<T>(list: readonly T[], keyGetter: (value: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>()
  for (const item of list) {
    const key = keyGetter(item)
    map.has(key) ? map.get(key)!.push(item) : map.set(key, [item])
  }
  return map
}

export const FilterMenu: React.FC<FilterMenuProps> = memo(({ filters, activeFilters, setActiveFilters, clearAll }) => {
  const isTablet = useMediaQuery('screen and (max-width: 767px)')
  const [isOpen, setIsOpen] = useState(!isTablet)

  const filteredFilters = filters.filter((filter) => filter.options.length > 0)

  // const filteredFiltersGrouped = filteredFilters
  const groupedFilteredFilters = groupBy(filteredFilters, (v) => v.category ?? '')

  useEffect(() => {
    if (isTablet === true && isOpen === false) {
      setIsOpen(false)
    }
  }, [isTablet, isOpen])

  return (
    <Wrapper>
      <Header>
        <HeaderButton
          onClick={() => {
            if (isTablet) {
              setIsOpen(!isOpen)
            } else {
              setIsOpen(true)
            }
          }}
        >
          <div style={{ marginRight: '8px' }}>
            <IconLarge icon={faFilter} />
          </div>
          <span style={{ marginRight: '4px' }}>
            <Translate id="filters" />
          </span>
          <span className="grid-hidden grid-tablet-visible text--light">({activeFilters.length})</span>
          <div style={{ flex: 1 }} />
        </HeaderButton>

        <ButtonGhost onClick={() => clearAll()}>
          <Translate id="clearAll" />
        </ButtonGhost>
      </Header>

      <Collapse isOpened={isOpen}>
        {filteredFilters.length === 0 && (
          <NoFiltersText>
            <Translate id="noFilters" />
          </NoFiltersText>
        )}
        {Array.from(groupedFilteredFilters).map((group, index) => {
          return (
            <Fragment key={index}>
              {group[0] !== undefined && <h4>{group[0]}</h4>}
              {group[1].map((filter) => (
                <FilterSection
                  key={filter.id ?? filter.name}
                  title={filter.name}
                  initialOpen={activeFilters.some((f) => filter.options.some((o) => o.id === f))}
                >
                  <div style={{ padding: '12px 0 6px', borderBottom: '1px solid #ececec' }}>
                    {filter.options.map((option) => (
                      <Row key={option.id}>
                        <FieldCheckBox
                          value={activeFilters.includes(option.id)}
                          onChange={(newValue) =>
                            newValue === true
                              ? setActiveFilters([...activeFilters, option.id])
                              : setActiveFilters(activeFilters.filter((x) => x !== option.id))
                          }
                          label={option.name}
                        />

                        {option.count !== undefined && <FilterCount>{option.count}</FilterCount>}
                      </Row>
                    ))}
                  </div>
                </FilterSection>
              ))}
            </Fragment>
          )
        })}
      </Collapse>
    </Wrapper>
  )
})
